import type { ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";

/**
 * Tables stay tables on mobile.
 *
 * They scroll horizontally rather than reflowing into stacked cards. A card per
 * row destroys the column alignment that makes a list of orders scannable, and
 * multiplies the vertical distance between the first and last row.
 *
 * The negative inline margin bleeds the scroll region past the enclosing card's
 * padding on small screens, so the table reaches the edge of the viewport
 * instead of scrolling inside a 20px inset.
 */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-5 overflow-x-auto sm:mx-0">
      <table className="w-full min-w-[560px] border-collapse text-sm">{children}</table>
    </div>
  );
}

/** Sticky, translucent and blurred — a tint rather than a hard bar. */
export function THead({ children }: { children: ReactNode }) {
  return <thead className="sticky top-0 z-10 bg-ink-50/80 backdrop-blur-sm">{children}</thead>;
}

export function Th({ children, className = "", ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={`border-b border-line px-4 py-3 text-start text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-subtle ${className}`}
      {...props}
    >
      {children}
    </th>
  );
}

/** No zebra striping — separation comes from hairline borders and a calm hover. */
export function Tr({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <tr
      className={`border-b border-line-hairline transition-colors last:border-0 hover:bg-ink-50/70 ${className}`}
    >
      {children}
    </tr>
  );
}

/**
 * Numeric cells take `tabular-nums` but NO alignment class.
 *
 * Alignment comes from the document direction, so numbers sit at the
 * reading-start edge in both Arabic and English. Adding `text-end` would look
 * correct in RTL and wrong the moment the interface switches to English.
 */
export function Td({ children, className = "", ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={`px-4 py-3.5 text-ink-700 ${className}`} {...props}>
      {children}
    </td>
  );
}
